import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';

import {
    IconWrapper,
    ItemAction,
    ItemContainer,
    ItemContent,
    ItemDetails,
    ItemSubtitle,
    ItemTitle,
} from './timeline-styles';
import type { TimelineItemProps } from './timeline-types';

const hasContent = (value: ReactNode): boolean => {
    if (is.nullOrUndefined(value)) {
        return false;
    }

    if (is.string(value)) {
        return value.trim().length > 0;
    }

    return true;
};

export const TimelineItem: FunctionComponent<TimelineItemProps> = ({
    action,
    children,
    className,
    disabled = false,
    icon,
    iconPresentation = 'indicator',
    itemSubtitle,
    itemTitle,
    onClick,
    ...props
}) => {
    const isClickable = !is.nullOrUndefined(onClick) && !disabled;

    return (
        <ItemContainer
            className={className}
            disabled={disabled}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={
                isClickable
                    ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onClick();
                          }
                      }
                    : undefined
            }
            {...props}
        >
            <ItemContent>
                {hasContent(icon) ? (
                    iconPresentation === 'bare' ? (
                        <span className="flex h-[1.25rem] w-7xl shrink-0 items-center justify-center">{icon}</span>
                    ) : (
                        <IconWrapper>{icon}</IconWrapper>
                    )
                ) : null}
                <ItemDetails>
                    <ItemTitle>{itemTitle}</ItemTitle>
                    {hasContent(itemSubtitle) ? <ItemSubtitle>{itemSubtitle}</ItemSubtitle> : null}
                    {children}
                </ItemDetails>
            </ItemContent>
            {hasContent(action) ? <ItemAction>{action}</ItemAction> : null}
        </ItemContainer>
    );
};
